import React from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * ResultDialogs — the two AlertDialogs the Result page uses to confirm
 * destructive-ish actions (rename the plant, re-run Gemini identification).
 *
 * Extracted from Result.jsx (iter62). Fully-controlled: parent owns open
 * state + pending-value refs + submit handlers. Keeping both dialogs in one
 * file mirrors the AlertDialog usage pattern the Gallery already uses.
 */
export default function ResultDialogs({
  // Rename
  renameOpen,
  onRenameOpenChange,
  renameText,
  onRenameTextChange,
  renaming,
  onSubmitRename,
  // Retry
  retryConfirmOpen,
  onRetryConfirmOpenChange,
  retrying,
  onConfirmRetry,
  retryPlantName,
  // Delete-scan
  deleteConfirmOpen,
  onDeleteConfirmOpenChange,
  deleting,
  onConfirmDelete,
  deletePlantName,
}) {
  const { t } = useTranslation();

  return (
    <>
      <AlertDialog open={renameOpen} onOpenChange={(o) => !renaming && onRenameOpenChange(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("result.renameTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("result.renameDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1">
            <input
              type="text"
              value={renameText}
              onChange={(e) => onRenameTextChange(e.target.value)}
              maxLength={200}
              autoFocus
              data-testid="rename-input"
              aria-label={t("result.renameTitle")}
              placeholder={t("result.renamePlaceholder")}
              className="w-full bg-white border border-[#E8E5DF] rounded-2xl px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-leaf/30"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rename-cancel" disabled={renaming}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="rename-confirm"
              onClick={(e) => { e.preventDefault(); onSubmitRename(); }}
              disabled={renaming || !renameText.trim()}
              className="bg-leaf hover:bg-leaf-deep"
            >
              {renaming ? t("result.renaming") : t("result.renameConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={retryConfirmOpen} onOpenChange={(o) => !retrying && onRetryConfirmOpenChange(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("result.retryConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("result.retryConfirmDesc", { name: retryPlantName || t("gallery.card.unknown") })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="retry-cancel" disabled={retrying}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="retry-confirm"
              onClick={(e) => { e.preventDefault(); onConfirmRetry(); }}
              disabled={retrying}
              className="bg-leaf hover:bg-leaf-deep"
            >
              {t("result.retryConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete-scan confirmation — destructive, uses the rust palette.
          Only mounted when the parent has wired an `onConfirmDelete`
          so pages that reuse ResultDialogs without a delete flow don't
          get a phantom dialog. */}
      {typeof onConfirmDelete === "function" && (
        <AlertDialog
          open={!!deleteConfirmOpen}
          onOpenChange={(o) => !deleting && onDeleteConfirmOpenChange(o)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display" data-testid="delete-scan-title">
                {t("result.deleteScanTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("result.deleteScanDesc", { name: deletePlantName || t("gallery.card.unknown") })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-scan-cancel" disabled={deleting}>
                {t("common.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="delete-scan-confirm"
                onClick={(e) => { e.preventDefault(); onConfirmDelete(); }}
                disabled={deleting}
                className="bg-rust hover:bg-rust/90 font-bold uppercase tracking-wide"
              >
                {deleting ? t("result.scanDeleting") : t("common.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
